import { getApiBase } from '@/app/api-router';

export interface AircraftMeta {
  icao24: string;
  registration: string | null;
  manufacturer: string | null;
  model: string | null;
  owner: string | null;
  operator: string | null;
  category: 'civilian' | 'military' | 'government' | 'unknown';
  isMilitary: boolean;
}

const metaCache = new Map<string, { data: AircraftMeta; expiresAt: number }>();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const BATCH_SIZE = 20;
const BATCH_DELAY_MS = 1000; // 1 second between batches to avoid 429s
const MAX_BATCHES_PER_CALL = 5; // Cap at 100 aircraft per enrichment cycle
let rateLimitedUntil = 0; // Timestamp — skip requests until this time

// Tracks ICAO24s that returned no result to avoid repeat lookups
const negativeCache = new Set<string>();
const NEGATIVE_CACHE_MAX = 5000;

export function getCachedAircraftMeta(icao24: string): AircraftMeta | null {
  const entry = metaCache.get(icao24);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    metaCache.delete(icao24);
    return null;
  }
  return entry.data;
}

export async function enrichAircraft(
  icaoCodes: string[],
): Promise<Map<string, AircraftMeta>> {
  const result = new Map<string, AircraftMeta>();
  const uncached: string[] = [];

  for (const icao of icaoCodes) {
    const cached = getCachedAircraftMeta(icao);
    if (cached) {
      result.set(icao, cached);
    } else if (!negativeCache.has(icao)) {
      uncached.push(icao);
    }
  }

  if (uncached.length === 0) return result;

  // Back off if we were recently rate-limited
  if (Date.now() < rateLimitedUntil) return result;

  // Cap the number of batches per call to avoid hammering the API
  const toEnrich = uncached.slice(0, MAX_BATCHES_PER_CALL * BATCH_SIZE);

  for (let i = 0; i < toEnrich.length; i += BATCH_SIZE) {
    const batch = toEnrich.slice(i, i + BATCH_SIZE);

    // Delay between batches (skip for the first one)
    if (i > 0) {
      await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
    }

    try {
      const fetched = await fetchAircraftBatch(batch);
      const now = Date.now();

      for (const [icao, meta] of Object.entries(fetched)) {
        metaCache.set(icao, { data: meta, expiresAt: now + CACHE_TTL_MS });
        result.set(icao, meta);
      }

      for (const icao of batch) {
        if (!fetched[icao]) {
          negativeCache.add(icao);
          if (negativeCache.size > NEGATIVE_CACHE_MAX) {
            const keep = [...negativeCache].slice(-Math.floor(NEGATIVE_CACHE_MAX / 2));
            negativeCache.clear();
            for (const v of keep) negativeCache.add(v);
          }
        }
      }
    } catch (err) {
      // On 429, back off for 60 seconds instead of hammering
      const msg = String(err);
      if (msg.includes('429')) {
        rateLimitedUntil = Date.now() + 60_000;
        console.warn('[Sentinel] Aircraft enrichment rate-limited, backing off 60s');
        break;
      }
      console.warn('[Sentinel] Aircraft enrichment batch failed:', err);
    }
  }

  return result;
}

async function fetchAircraftBatch(
  icaoCodes: string[],
): Promise<Record<string, AircraftMeta>> {
  const url = `${getApiBase()}/api/data/wingbits?icao24=${icaoCodes.join(',')}`;
  const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
  if (!response.ok) {
    throw new Error(`Aircraft enrichment proxy returned ${response.status}`);
  }
  return response.json();
}

export function getEnrichmentCacheStats(): { cached: number; negativeCached: number } {
  return { cached: metaCache.size, negativeCached: negativeCache.size };
}
